"use client";
import React from "react";
import { Caveat } from "next/font/google";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* ─────────────────────────────────────────────────────────────
   METAL FABRICATOR: CRAFT CUSTOM QUOTES SECTION
   Exact reproduction of Odoo's Metal Fabrication Quotation / Sales Order UI
   ───────────────────────────────────────────────────────────── */
export function MetalFabricatorCraftCustomQuotesSection({ section }) {
  const title = section?.title || "Craft custom quotes";
  const subtext = section?.subtext || "Generate quotations and define the most accurate price for your clients with a built-in quote calculator.";

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* LEFT COLUMN: SALES ORDER / QUOTATION PDF DOCUMENT MOCKUP CARD */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative w-full flex justify-center lg:justify-start"
          >
            {/* White Paper Document Window */}
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 text-slate-800 text-[9.5px] overflow-hidden w-full p-4 sm:p-5 font-sans">

              
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4">
                
                {/* Left Sidebar Column (3.5 cols) */}
                <div className="md:col-span-4 space-y-2 border-b md:border-b-0 md:border-r border-slate-100 pb-2 md:pb-0 md:pr-3">
                  
                  {/* Top Big Amount */}
                  <div className="border-b border-slate-100 pb-1.5">
                    <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-tight">34,238.77 €</h3>
                    <button className="mt-1 text-[8px] font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded px-2 py-0.5 hover:bg-slate-200 transition-colors w-full text-center">
                      👁 View Details
                    </button>
                  </div>

                  {/* Document Metadata */}
                  <div className="space-y-0.5 text-[8px] text-slate-500">
                    <p className="font-semibold text-slate-800 text-[9px]">Sales Order - S00003</p>
                    <p className="text-sky-600 cursor-pointer hover:underline">Communication history</p>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-1 border-t border-slate-100 pt-1.5 text-[8px]">
                    <span className="text-slate-400 font-semibold block text-[7.5px] uppercase tracking-wider">Your contact</span>
                    <div className="flex items-center gap-1.5">
                      <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-4 h-4 rounded-full object-cover" />
                      <div>
                        <strong className="text-slate-800 block text-[8px]">Jason Calder</strong>
                        <span className="text-sky-600 text-[7.5px] cursor-pointer hover:underline">Send message</span>
                      </div>
                    </div>
                  </div>

                  {/* Connect with software button */}
                  <div className="pt-1">
                    <button className="w-full bg-[#1D5B96] hover:bg-[#164775] text-white font-semibold py-1 rounded text-[8px] transition-colors shadow-sm text-center">
                      Connect with your software!
                    </button>
                    <span className="text-[7.5px] text-slate-400 block text-center mt-1">Powered by odoo</span>
                  </div>

                </div>

                {/* Main Quotation Content Column (8 cols) */}
                <div className="md:col-span-8 space-y-2 text-[8px]">
                  
                  {/* Title & Customer Address Header */}
                  <div className="flex flex-wrap items-start justify-between border-b border-slate-100 pb-1.5 gap-1.5">
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-slate-900">Sales Order - <i className="font-serif">S00003</i></h4>
                      <div className="mt-0.5 space-y-0.5 text-slate-500 text-[8px]">
                        <strong className="text-slate-700 block text-[8px]">Sale Information</strong>
                        <p>Order Date: <span className="font-medium text-slate-800">09/12/2025</span></p>
                      </div>
                    </div>

                    <div className="text-right text-[8px] text-slate-500">
                      <strong className="text-slate-700 block text-[8px]">Invoicing and Shipping Address</strong>
                      <p className="font-bold text-slate-800">Stephan Frosh</p>
                      <p>Rue de l&apos;Ange 57</p>
                      <p>5000 Namur, Belgium</p>
                      <p className="text-sky-600 truncate max-w-[120px]">stephan@ironspeak.com</p>
                    </div>
                  </div>

                  {/* Status Pills Row */}
                  <div className="grid grid-cols-2 gap-1.5 bg-slate-50/80 p-1 rounded-lg border border-slate-100 text-[7.5px]">
                    <div>
                      <span className="text-slate-400 block font-semibold mb-0.5">Last Invoices</span>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-bold text-slate-700">INV/2025/00001</span>
                        <span className="bg-sky-50 text-sky-700 border border-sky-200 px-1 py-0.2 rounded-full font-semibold">Waiting Payment</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-semibold mb-0.5">Last Delivery Orders</span>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-bold text-slate-700">WH/OUT/00303</span>
                        <span className="bg-teal-50 text-teal-700 border border-teal-200 px-1 py-0.2 rounded-full font-semibold">Preparation</span>
                      </div>
                    </div>
                  </div>

                  {/* Line Items Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-[8px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-400 font-semibold text-[7.5px]">
                          <th className="py-0.5">Products</th>
                          <th className="py-0.5 text-right">Quantity</th>
                          <th className="py-0.5 text-right">Unit Price</th>
                          <th className="py-0.5 text-right">Taxes</th>
                          <th className="py-0.5 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-700 text-[7.5px]">
                        {/* Services Group Header */}
                        <tr className="bg-slate-50/80 font-bold text-slate-900">
                          <td colSpan={4} className="py-0.5">Services</td>
                          <td className="py-0.5 text-right">14,718.00 €</td>
                        </tr>
                        <tr>
                          <td className="py-0.5 pl-1 text-slate-600">Include on-site installation &amp; transport</td>
                          <td className="py-0.5 text-right">1.00 Units</td>
                          <td className="py-0.5 text-right">14,718.00 €</td>
                          <td className="py-0.5 text-right">21%</td>
                          <td className="py-0.5 text-right font-medium">14,718.00 €</td>
                        </tr>

                        {/* Products Group Header */}
                        <tr className="bg-slate-50/80 font-bold text-slate-900">
                          <td colSpan={4} className="py-0.5">Products</td>
                          <td className="py-0.5 text-right">13,578.50 €</td>
                        </tr>
                        <tr>
                          <td className="py-0.5 pl-1">
                            <span className="font-semibold text-slate-900 block">Custom Product</span>
                            <span className="text-slate-400 text-[7px]">Straight stairs</span>
                          </td>
                          <td className="py-0.5 text-right">2.00 Units</td>
                          <td className="py-0.5 text-right">4,234.75 €</td>
                          <td className="py-0.5 text-right">21%</td>
                          <td className="py-0.5 text-right font-medium">8,469.50 €</td>
                        </tr>
                        <tr>
                          <td className="py-0.5 pl-1">
                            <span className="font-semibold text-slate-900 block">Custom Product</span>
                            <span className="text-slate-400 text-[7px]">Quarter turn stairs</span>
                          </td>
                          <td className="py-0.5 text-right">1.00 Units</td>
                          <td className="py-0.5 text-right">1,007.50 €</td>
                          <td className="py-0.5 text-right">21%</td>
                          <td className="py-0.5 text-right font-medium">1,007.50 €</td>
                        </tr>
                        <tr>
                          <td className="py-0.5 pl-1">
                            <span className="font-semibold text-slate-900 block">Custom Product</span>
                            <span className="text-slate-400 text-[7px]">Stairs 3 levels</span>
                          </td>
                          <td className="py-0.5 text-right">1.00 Units</td>
                          <td className="py-0.5 text-right">4,101.50 €</td>
                          <td className="py-0.5 text-right">21%</td>
                          <td className="py-0.5 text-right font-medium">4,101.50 €</td>
                        </tr>

                        {/* Down Payments Group Header */}
                        <tr className="bg-slate-50/80 font-bold text-slate-900">
                          <td colSpan={4} className="py-0.5">Down Payments</td>
                          <td className="py-0.5 text-right">0.00 €</td>
                        </tr>
                        <tr>
                          <td className="py-0.5 pl-1 text-slate-500 text-[7px]">Down Payment (ref: ***000/3000/00303*** on 09/12/2025)</td>
                          <td className="py-0.5 text-right">0.00</td>
                          <td className="py-0.5 text-right">8,488.65 €</td>
                          <td className="py-0.5 text-right">21%</td>
                          <td className="py-0.5 text-right font-medium">0.00 €</td>
                        </tr>

                        {/* Total Row */}
                        <tr className="border-t border-slate-200 font-bold text-slate-900 text-[9px]">
                          <td colSpan={4} className="py-1 text-right">Untaxed Amount</td>
                          <td className="py-1 text-right text-slate-900 font-bold">28,296.50 €</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                </div>

              </div>

            </div>
          </motion.div>

          {/* RIGHT COLUMN: HANDWRITTEN TITLE & COPYWRITING WITH LOOP ARROW */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 space-y-6 text-left relative"
          >
            {/* Title with Yellow Highlight Brush on "quotes" */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1] relative`}>
              <div>Craft custom</div>
              <div>
                <span className="relative inline-block px-2 z-10">
                  quotes
                  <span className="absolute bottom-1 left-0 w-full h-4 sm:h-5 bg-[#F59E0B] -z-10 rounded transform -rotate-1 opacity-90" />
                </span>
              </div>
            </h2>

            {/* Subtext Copy */}
            <p className="text-xl text-gray-300 leading-relaxed font-normal">
              <strong className="text-white font-bold">Generate quotations</strong> and define the most accurate price for your clients with a built-in quote calculator.
            </p>


            {/* Bottom Right Loop Arrow Doodle */}
            <div className="pt-2 flex justify-center lg:justify-start">
              <svg width="90" height="60" viewBox="0 0 120 80" fill="none" className="text-slate-300 opacity-80">
                <path
                  d="M 20 10 Q 80 10, 60 50 Q 40 80, 80 70 M 80 70 L 68 62 M 80 70 L 72 78"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   2. METAL FABRICATOR: TIMELY DELIVERY SECTION
   Exact reproduction of Odoo's Manufacturing Order (MO Overview) Table UI
   ───────────────────────────────────────────────────────────── */
export function MetalFabricatorTimelyDeliverySection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* TOP PERSONA SPEECH BUBBLE CALLOUT */}
        <div className="w-full flex justify-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center gap-2 sm:gap-3 max-w-full px-2">
            <img
              className="rounded-full border border-white/20 shadow-lg flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 object-cover"
              alt="Odoo Specialist"
              src="/odoo/snippets/s_wd_persona/bubu.webp"
            />
            <div className="bg-white text-slate-800 rounded-full shadow-2xl p-2.5 sm:p-3 px-5 sm:px-8 border border-slate-100 text-center">
              <i className="text-slate-800 text-xs sm:text-base font-semibold block leading-snug">
                Double the speed of the quotation process.
              </i>
            </div>
          </div>
        </div>

        {/* CENTERED SECTION HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-14 text-center max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Timely delivery </span>
            <span className="relative inline-block px-1.5">
              starts
              <svg
                className="absolute -bottom-1.5 left-0 w-full h-2.5 text-sky-400 overflow-visible"
                viewBox="0 0 100 12"
                fill="none"
              >
                <path
                  d="M 4 6 Q 50 1, 96 6"
                  stroke="#38BDF8"
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
            <span> with timely replenishment</span>
          </h2>

          <p className="text-xl text-gray-300 leading-relaxed font-normal">
            <strong className="text-white font-bold">A complete overview of your production process</strong> to help you manage everything from cost estimation to the expected arrival time of every component.
          </p>
        </motion.div>

        {/* MANUFACTURING ORDERS (MO OVERVIEW) TABLE MOCKUP CARD */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="w-full flex justify-center"
        >
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[11px] w-full max-w-5xl p-4 sm:p-6 font-sans">

            
            {/* Top Toolbar */}
            <div className="flex flex-wrap items-center justify-between border-b border-slate-100 pb-2 mb-3 gap-2">
              <div className="flex items-center gap-1.5">
                <button className="bg-[#6B4C63] hover:bg-[#583D51] text-white font-semibold px-2.5 py-0.5 rounded text-[10px] transition-colors shadow-sm">
                  Print
                </button>
                <button className="bg-[#6B4C63] hover:bg-[#583D51] text-white font-semibold px-2.5 py-0.5 rounded text-[10px] transition-colors shadow-sm">
                  Unfold
                </button>
                
                <div className="text-[10px] text-slate-500 flex items-center gap-1 font-medium ml-1.5">
                  <span className="text-[#00A09D] font-semibold">Manufacturing Ord...</span>
                  <span>/</span>
                  <span className="text-[#00A09D] font-semibold">WH/MO/000...</span>
                  <span className="text-slate-700">MO Overview</span>
                </div>
              </div>

              <div className="bg-slate-100 border border-slate-200 rounded px-2.5 py-0.5 text-[10px] text-slate-700 font-medium flex items-center gap-1 cursor-pointer">
                <span>▼ Display</span>
              </div>
            </div>

            {/* MO Overview Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-[11px]">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 font-bold text-[10px]">
                    <th className="py-1 pl-2"></th>
                    <th className="py-1 text-center">Status</th>
                    <th className="py-1 text-center">Quantity</th>
                    <th className="py-1 text-center">Free to use / On Hand</th>
                    <th className="py-1 text-center">Reserved</th>
                    <th className="py-1 text-center">Receipt</th>
                    <th className="py-1 text-right">MO Cost</th>
                    <th className="py-1 text-right pr-2">BoM Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 text-[10px]">
                  {/* Row 1: Custom Product */}
                  <tr>
                    <td className="py-1.5 pl-2 font-bold text-[#00A09D]">Custom Product</td>
                    <td className="py-1.5 text-center">
                      <span className="bg-[#00A09D] text-white text-[9px] font-bold px-2 py-0.2 rounded-full inline-block">
                        Ready
                      </span>
                    </td>
                    <td className="py-1.5 text-center font-medium">1.00</td>
                    <td className="py-1.5 text-center text-slate-400">-</td>
                    <td className="py-1.5 text-center font-medium">0.00</td>
                    <td className="py-1.5 text-center text-amber-600 font-semibold flex items-center justify-center gap-1">
                      <span>Expected 09/12/2025</span>
                      <span className="text-[9px]">🏔</span>
                    </td>
                    <td className="py-1.5 text-right font-semibold text-rose-500">130.00 €</td>
                    <td className="py-1.5 text-right pr-2 font-medium">0.00 €</td>
                  </tr>

                  {/* Row 2: Custom Laser Cutting INOX */}
                  <tr>
                    <td className="py-1.5 pl-4 font-medium text-[#00A09D] flex items-center gap-1">
                      <span className="text-slate-400 text-[8px]">▶</span>
                      <span>Custom Laser Cutting INOX</span>
                    </td>
                    <td className="py-1.5 text-center"></td>
                    <td className="py-1.5 text-center font-medium">1.00</td>
                    <td className="py-1.5 text-center text-slate-400">-</td>
                    <td className="py-1.5 text-center font-medium">0.00</td>
                    <td className="py-1.5 text-center text-emerald-600 font-semibold flex items-center justify-center gap-1">
                      <span>Available</span>
                      <span className="text-[9px]">🏔</span>
                    </td>
                    <td className="py-1.5 text-right font-semibold text-rose-500">10.00 €</td>
                    <td className="py-1.5 text-right pr-2 font-medium">0.00 €</td>
                  </tr>

                  {/* Row 3: Aluminium Bar 14*14*220 */}
                  <tr>
                    <td className="py-1.5 pl-4 font-medium text-[#00A09D] flex items-center gap-1">
                      <span className="text-slate-400 text-[8px]">▶</span>
                      <span>Aluminium Bar 14*14*220</span>
                    </td>
                    <td className="py-1.5 text-center"></td>
                    <td className="py-1.5 text-center font-medium">1.00</td>
                    <td className="py-1.5 text-center text-slate-400">-</td>
                    <td className="py-1.5 text-center font-medium">0.00</td>
                    <td className="py-1.5 text-center text-emerald-600 font-semibold flex items-center justify-center gap-1">
                      <span>Available</span>
                      <span className="text-[9px]">🏔</span>
                    </td>
                    <td className="py-1.5 text-right font-semibold text-rose-500">120.00 €</td>
                    <td className="py-1.5 text-right pr-2 font-medium">0.00 €</td>
                  </tr>

                  {/* Total Row: Unit Cost */}
                  <tr className="border-t border-slate-200 font-bold text-slate-800 bg-slate-50/50">
                    <td colSpan={6} className="py-1.5 text-right font-bold text-slate-600">Unit Cost</td>
                    <td className="py-1.5 text-right font-bold text-slate-900">130.00 €</td>
                    <td className="py-1.5 text-right pr-2 font-bold text-slate-900">0.00 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. METAL FABRICATOR: TRACK YOUR PURCHASE SECTION
   Exact reproduction of Odoo's Purchase RFQ Dashboard & Orders Table UI
   ───────────────────────────────────────────────────────────── */
export function MetalFabricatorTrackPurchaseSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* CENTERED SECTION HEADER WITH HANDWRITTEN TITLE & ORANGE DOTTED LINE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-14 text-center max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Track your </span>
            <span className="relative inline-block px-1">
              purchase
              <svg
                className="absolute -bottom-1.5 left-0 w-full h-2.5 text-amber-400 overflow-visible"
                viewBox="0 0 100 12"
                fill="none"
              >
                <path
                  d="M 4 6 Q 50 2, 96 6"
                  stroke="#F59E0B"
                  strokeWidth="5"
                  strokeDasharray="2 6"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h2>

          <p className="text-xl text-gray-300 leading-relaxed">
            <strong className="text-white font-bold">Manage, group, compare</strong> and <strong className="text-white font-bold">validate</strong> your purchases and automate the creation process.
          </p>
        </motion.div>

        {/* PURCHASE DASHBOARD MOCKUP CARD WITH BASKET DOODLE */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="w-full flex justify-center items-center relative"
        >
          {/* Main White Dashboard Window */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[10px] sm:text-[11px] w-full max-w-5xl p-4 sm:p-6 font-sans relative">

            
            {/* Top KPI Metrics Cards Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-1.5 mb-3 text-center">
              
              {/* Metric 1: New */}
              <div className="bg-[#D0ECF4] text-[#0A5666] p-1.5 sm:p-2 rounded-lg border border-sky-200">
                <span className="text-sm font-bold block">2</span>
                <span className="text-[9px] font-semibold block">New</span>
              </div>

              {/* Metric 2: RFQ Sent */}
              <div className="bg-slate-100 text-slate-700 p-1.5 sm:p-2 rounded-lg border border-slate-200">
                <span className="text-sm font-bold block">2</span>
                <span className="text-[9px] font-semibold block">RFQ Sent</span>
              </div>

              {/* Metric 3: Late RFQ */}
              <div className="bg-slate-100 text-slate-700 p-1.5 sm:p-2 rounded-lg border border-slate-200">
                <span className="text-sm font-bold block">0</span>
                <span className="text-[9px] font-semibold block">Late RFQ</span>
              </div>

              {/* Metric 4: Not Acknowledged */}
              <div className="bg-[#D0ECF4] text-[#0A5666] p-1.5 sm:p-2 rounded-lg border border-sky-200">
                <span className="text-sm font-bold block">3</span>
                <span className="text-[8.5px] font-semibold block leading-tight">Not Acknowledged</span>
              </div>

              {/* Metric 5: Late Receipt */}
              <div className="bg-[#FCD8D9] text-[#9E2A2B] p-1.5 sm:p-2 rounded-lg border border-rose-200">
                <span className="text-sm font-bold block">2</span>
                <span className="text-[9px] font-semibold block">Late Receipt</span>
              </div>

              {/* Metric 6: OTD */}
              <div className="bg-slate-50 text-slate-700 p-1.5 sm:p-2 rounded-lg border border-slate-200">
                <span className="text-sm font-bold block">70 %</span>
                <span className="text-[9px] font-semibold block">OTD</span>
              </div>

              {/* Metric 7: Days to Order */}
              <div className="bg-slate-50 text-slate-700 p-1.5 sm:p-2 rounded-lg border border-slate-200">
                <span className="text-sm font-bold block">3.50</span>
                <span className="text-[9px] font-semibold block">Days to Order</span>
              </div>

            </div>

            {/* Purchase RFQ Orders Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-[10px] sm:text-[11px]">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600 font-bold text-[9.5px] bg-slate-50/50">
                    <th className="py-1 pl-2 w-6">☐</th>
                    <th className="py-1 font-bold">Reference</th>
                    <th className="py-1">Vendor</th>
                    <th className="py-1">Buyer</th>
                    <th className="py-1">Order Deadline</th>
                    <th className="py-1 text-center">Activities</th>
                    <th className="py-1 text-right">Total</th>
                    <th className="py-1 text-center">Status</th>
                    <th className="py-1 text-right pr-2">⇄</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 text-[10px]">
                  
                  {/* Row 1: P00007 / Precision Alloy Systems */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-1 pl-2 text-slate-400">☐ ☆</td>
                    <td className="py-1 font-bold text-sky-600 cursor-pointer">P00007</td>
                    <td className="py-1 text-sky-600 font-medium cursor-pointer">Precision Alloy Systems</td>
                    <td className="py-1">
                      <div className="flex items-center gap-1">
                        <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3.5 h-3.5 rounded-full object-cover" />
                        <span className="text-sky-600 font-medium text-[10px]">Jason Calder</span>
                      </div>
                    </td>
                    <td className="py-1 text-sky-600 font-medium">In 15 days</td>
                    <td className="py-1 text-center text-slate-400 text-[9px]">🕒</td>
                    <td className="py-1 text-right font-semibold text-slate-800">659.45 €</td>
                    <td className="py-1 text-center">
                      <span className="bg-sky-500 text-white font-bold text-[8.5px] px-1.5 py-0.2 rounded-full inline-block">RFQ</span>
                    </td>
                    <td className="py-1 text-right pr-2"></td>
                  </tr>

                  {/* Row 2: P00006 / ForgeTech */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-1 pl-2 text-slate-400">☐ ☆</td>
                    <td className="py-1 font-bold text-slate-900">P00006</td>
                    <td className="py-1 font-medium text-slate-800">ForgeTech</td>
                    <td className="py-1">
                      <div className="flex items-center gap-1">
                        <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3.5 h-3.5 rounded-full object-cover" />
                        <span className="text-slate-800 font-medium text-[10px]">Jason Calder</span>
                      </div>
                    </td>
                    <td className="py-1 text-slate-400">-</td>
                    <td className="py-1 text-center text-slate-400 text-[9px]">🕒</td>
                    <td className="py-1 text-right font-semibold text-slate-800">4,525.40 €</td>
                    <td className="py-1 text-center">
                      <span className="bg-emerald-600 text-white font-bold text-[8.5px] px-1.5 py-0.2 rounded-full inline-block">Purchase Order</span>
                    </td>
                    <td className="py-1 text-right pr-2"></td>
                  </tr>

                  {/* Row 3: P00005 / BlackForge Engineering */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-1 pl-2 text-slate-400">☐ ☆</td>
                    <td className="py-1 font-bold text-slate-900">P00005</td>
                    <td className="py-1 font-medium text-slate-800">BlackForge Engineering</td>
                    <td className="py-1">
                      <div className="flex items-center gap-1">
                        <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3.5 h-3.5 rounded-full object-cover" />
                        <span className="text-slate-800 font-medium text-[10px]">Jason Calder</span>
                      </div>
                    </td>
                    <td className="py-1 text-slate-400">-</td>
                    <td className="py-1 text-center text-slate-400 text-[9px]">🕒</td>
                    <td className="py-1 text-right font-semibold text-slate-800">9,634.02 €</td>
                    <td className="py-1 text-center">
                      <span className="bg-emerald-600 text-white font-bold text-[8.5px] px-1.5 py-0.2 rounded-full inline-block">Purchase Order</span>
                    </td>
                    <td className="py-1 text-right pr-2"></td>
                  </tr>

                  {/* Row 4: P00004 / Ironcrest */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-1 pl-2 text-slate-400">☐ ☆</td>
                    <td className="py-1 font-bold text-sky-600 cursor-pointer">P00004</td>
                    <td className="py-1 text-sky-600 font-medium cursor-pointer">Ironcrest</td>
                    <td className="py-1">
                      <div className="flex items-center gap-1">
                        <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3.5 h-3.5 rounded-full object-cover" />
                        <span className="text-sky-600 font-medium text-[10px]">Jason Calder</span>
                      </div>
                    </td>
                    <td className="py-1 text-sky-600 font-medium">In 14 days</td>
                    <td className="py-1 text-center text-slate-400 text-[9px]">🕒</td>
                    <td className="py-1 text-right font-semibold text-slate-800">7,260.00 €</td>
                    <td className="py-1 text-center">
                      <span className="bg-sky-500 text-white font-bold text-[8.5px] px-1.5 py-0.2 rounded-full inline-block">RFQ</span>
                    </td>
                    <td className="py-1 text-right pr-2"></td>
                  </tr>

                  {/* Row 5: P00003 / Precision Alloy Systems */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-1 pl-2 text-slate-400">☐ ☆</td>
                    <td className="py-1 font-bold text-sky-600 cursor-pointer">P00003</td>
                    <td className="py-1 text-sky-600 font-medium cursor-pointer">Precision Alloy Systems</td>
                    <td className="py-1">
                      <div className="flex items-center gap-1">
                        <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3.5 h-3.5 rounded-full object-cover" />
                        <span className="text-sky-600 font-medium text-[10px]">Jason Calder</span>
                      </div>
                    </td>
                    <td className="py-1 text-sky-600 font-medium">In 8 days</td>
                    <td className="py-1 text-center text-slate-400 text-[9px]">🕒</td>
                    <td className="py-1 text-right font-semibold text-slate-800">2,178.00 €</td>
                    <td className="py-1 text-center">
                      <span className="bg-teal-600 text-white font-bold text-[8.5px] px-1.5 py-0.2 rounded-full inline-block">RFQ Sent</span>
                    </td>
                    <td className="py-1 text-right pr-2"></td>
                  </tr>
                  
                  {/* Row 7: P00001 / ForgeTech */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-1 pl-2 text-slate-400">☐ ☆</td>
                    <td className="py-1 font-bold text-slate-900">P00001</td>
                    <td className="py-1 font-medium text-slate-800">ForgeTech</td>
                    <td className="py-1">
                      <div className="flex items-center gap-1">
                        <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3.5 h-3.5 rounded-full object-cover" />
                        <span className="text-slate-800 font-medium text-[10px]">Jason Calder</span>
                      </div>
                    </td>
                    <td className="py-1 text-slate-400">-</td>
                    <td className="py-1 text-center text-slate-400 text-[9px]">🕒</td>
                    <td className="py-1 text-right font-semibold text-slate-800">181.50 €</td>
                    <td className="py-1 text-center">
                      <span className="bg-emerald-600 text-white font-bold text-[8.5px] px-1.5 py-0.2 rounded-full inline-block">Purchase Order</span>
                    </td>
                    <td className="py-1 text-right pr-2"></td>
                  </tr>

                  {/* Total Row */}
                  <tr className="border-t border-slate-200 font-bold text-slate-800 bg-slate-50/50">
                    <td colSpan={6} className="py-1.5 text-right font-bold text-slate-600">Total</td>
                    <td className="py-1.5 text-right font-bold text-slate-900">25,503.17 €</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>

          {/* Right Orange Basket Doodle */}
          <div className="hidden lg:block absolute -right-4 top-1/2 transform -translate-y-1/2 text-amber-500 select-none pointer-events-none">
            <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
              <path
                d="M 12 24 L 52 24 L 46 52 L 18 52 Z"
                fill="#F1BF38"
                stroke="#D97706"
                strokeWidth="4"
                strokeLinejoin="round"
              />
              <path d="M 24 24 L 20 12 L 32 12" stroke="#D97706" strokeWidth="4" strokeLinecap="round" />
              <path d="M 40 24 L 44 12 L 32 12" stroke="#D97706" strokeWidth="4" strokeLinecap="round" />
              <line x1="24" y1="32" x2="24" y2="44" stroke="#D97706" strokeWidth="3" />
              <line x1="32" y1="32" x2="32" y2="44" stroke="#D97706" strokeWidth="3" />
              <line x1="40" y1="32" x2="40" y2="44" stroke="#D97706" strokeWidth="3" />
            </svg>
          </div>
        </motion.div>

        {/* BOTTOM PERSONA SPEECH BUBBLE CALLOUT */}
        <div className="w-full flex justify-center mt-6 sm:mt-8">
          <div className="flex items-center justify-center gap-2 sm:gap-3 max-w-full px-2">
            <img
              className="rounded-full border border-white/20 shadow-lg flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 object-cover"
              alt="Odoo Specialist"
              src="/odoo/snippets/s_wd_persona/christina.webp"
            />
            <div className="bg-white/10 text-slate-800 rounded-full shadow-2xl p-2 sm:p-2.5 px-4 sm:px-6 border border-slate-100 text-center">
              <i className="text-white text-xs sm:text-sm font-semibold block leading-snug">
                From simple task to complex projects, ensure efficient follow-up.
              </i>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   4. METAL FABRICATOR: EFFORTLESS PROJECT MANAGEMENT SECTION
   Exact reproduction of Odoo's Project Kanban Board UI
   ───────────────────────────────────────────────────────────── */
export function MetalFabricatorEffortlessProjectManagementSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* LEFT COLUMN: HANDWRITTEN TITLE & COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-6 text-left relative"
          >
            {/* Title with Pink/Red Underline under "Effortless" */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1] relative`}>
              <div>
                <span className="relative inline-block px-1">
                  Effortless
                  <span className="absolute bottom-1 left-0 w-full h-3 bg-[#FF6B6B] -z-10 rounded-full opacity-90" />
                </span>
                <span> project</span>
              </div>
              <div>management</div>
            </h2>

            {/* Subtext Copy */}
            <p className="text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">Customize your project overview</strong> to track only the essential information. Get everything you need to stay in control without the information overload.
            </p>
          </motion.div>

          {/* RIGHT COLUMN: ODOO PROJECT KANBAN BOARD MOCKUP CARD */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 relative w-full flex justify-center lg:justify-end"
          >
            {/* White Window Container */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[10px] sm:text-[11px] w-full p-4 sm:p-6 font-sans">

              
              {/* Odoo Top Header Navigation Bar */}
              <div className="border-b border-slate-100 pb-2 mb-2.5 space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-1 text-[9.5px] text-slate-600 font-medium">
                  <div className="flex items-center gap-1.5">
                    <span className="text-emerald-600 font-bold flex items-center gap-0.5">✓ Project</span>
                    <span className="cursor-pointer hover:text-slate-900">Projects</span>
                    <span className="cursor-pointer hover:text-slate-900">Tasks</span>
                    <span className="cursor-pointer hover:text-slate-900">Reporting</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[8.5px] text-slate-500">
                    <span className="bg-purple-100 text-purple-700 px-1 rounded font-bold">AI</span>
                    <span>💬</span>
                    <span>⚙️</span>
                    <span className="font-bold text-slate-800">Titan Metals</span>
                    <img src="/odoo/unsplash/avatars/u3.jpg" alt="Titan Metals" className="w-3.5 h-3.5 rounded-full object-cover" />
                  </div>
                </div>

                {/* Sub Header / Breadcrumb & Search */}
                <div className="flex flex-wrap items-center justify-between gap-1 pt-0.5">
                  <div className="flex items-center gap-1 text-[9px]">
                    <span className="bg-[#7C5A76] text-white px-1.5 py-0.2 rounded font-semibold text-[8px]">New</span>
                    <span className="text-slate-500 font-medium truncate max-w-[180px]">Projects / <strong className="text-slate-800">S00001 - Template Project</strong></span>
                  </div>
                  <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[8.5px] text-slate-500">
                    <span>🔍</span>
                    <span className="bg-purple-100 text-purple-800 px-1 rounded font-semibold text-[8px]">Open ✖</span>
                  </div>
                </div>
              </div>

              {/* Kanban Board Columns Container */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                
                {/* Column 1: Design & admin */}
                <div className="space-y-1.5">
                  {/* Column Header */}
                  <div className="flex items-center justify-between text-[9.5px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>Design &amp; admin</span>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[8.5px]">3</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-3/4 bg-emerald-500 h-full" />
                    <div className="w-1/4 bg-amber-400 h-full" />
                  </div>

                  {/* Card 1: Material ordering */}
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 shadow-sm space-y-1 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <strong className="text-slate-900 text-[9.5px] block font-bold">Material ordering</strong>
                        <span className="text-slate-400 text-[8px]">IronPeak Studio</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[7px] px-1 rounded font-semibold">Admin</span>
                      <span className="bg-sky-50 text-sky-700 border border-sky-200 text-[7px] px-1 rounded font-semibold">Purchase</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[7.5px]">
                      <span className="text-amber-400">⭐⭐</span>
                      <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3 h-3 rounded-full object-cover" />
                    </div>
                  </div>

                  {/* Card 2: Review BOM - counter */}
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 shadow-sm space-y-1 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <strong className="text-slate-900 text-[9.5px] block font-bold">Review BOM</strong>
                        <span className="text-slate-400 text-[8px]">IronPeak Studio</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-amber-50 text-amber-700 border border-amber-200 text-[7px] px-1 rounded font-semibold">CAD</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[7.5px]">
                      <span className="text-amber-400">⭐⭐</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    </div>
                  </div>

                  {/* Card 3: Design presentation */}
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 shadow-sm space-y-1 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <strong className="text-slate-900 text-[9.5px] block font-bold">Design presentation</strong>
                        <span className="text-slate-400 text-[8px]">IronPeak Studio</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[7px] px-1 rounded font-semibold">Admin</span>
                      <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[7px] px-1 rounded font-semibold">Client</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[7.5px]">
                      <span className="text-slate-300">☆☆</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    </div>
                  </div>
                </div>

                {/* Column 2: Ongoing */}
                <div className="space-y-1.5">
                  {/* Column Header */}
                  <div className="flex items-center justify-between text-[9.5px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>Ongoing</span>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[8.5px]">2</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-1/2 bg-slate-300 h-full" />
                  </div>

                  {/* Card 1: Metalwork planning */}
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 shadow-sm space-y-1 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <strong className="text-slate-900 text-[9.5px] block font-bold">Metalwork planning</strong>
                        <span className="text-slate-400 text-[8px]">IronPeak Studio</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[7px] px-1 rounded font-semibold">Admin</span>
                      <span className="bg-orange-50 text-orange-700 border border-orange-200 text-[7px] px-1 rounded font-semibold">Workshop</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[7.5px]">
                      <span className="text-amber-400">⭐⭐</span>
                      <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3 h-3 rounded-full object-cover" />
                    </div>
                  </div>

                  {/* Card 2: Woodwork planning */}
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 shadow-sm space-y-1 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <strong className="text-slate-900 text-[9.5px] block font-bold">Woodwork planning</strong>
                        <span className="text-slate-400 text-[8px]">IronPeak Studio</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[7px] px-1 rounded font-semibold">Admin</span>
                      <span className="bg-orange-50 text-orange-700 border border-orange-200 text-[7px] px-1 rounded font-semibold">Workshop</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[7.5px]">
                      <span className="text-slate-300">☆☆</span>
                      <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3 h-3 rounded-full object-cover" />
                    </div>
                  </div>
                </div>

                {/* Column 3: Assembly & onsite work */}
                <div className="space-y-1.5">
                  {/* Column Header */}
                  <div className="flex items-center justify-between text-[9.5px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>Assembly &amp; onsite</span>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[8.5px]">2</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-2/3 bg-amber-400 h-full" />
                  </div>

                  {/* Card 1: Marble samples review */}
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 shadow-sm space-y-1 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <strong className="text-slate-900 text-[9.5px] block font-bold">Marble samples review</strong>
                        <span className="text-slate-400 text-[8px]">Tom Wilson</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-sky-50 text-sky-700 border border-sky-200 text-[7px] px-1 rounded font-semibold">Purchase</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[7.5px]">
                      <span className="text-amber-400">⭐⭐⭐</span>
                      <span className="text-amber-500 font-bold text-[8.5px]">⚠️</span>
                    </div>
                  </div>

                  {/* Card 2: Site visit */}
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 shadow-sm space-y-1 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <strong className="text-slate-900 text-[9.5px] block font-bold">Site visit</strong>
                        <span className="text-slate-400 text-[8px]">IronPeak Studio</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-purple-50 text-purple-700 border border-purple-200 text-[7px] px-1 rounded font-semibold">Onsite</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[7.5px]">
                      <span className="text-slate-300">☆☆</span>
                      <img src="/odoo/unsplash/avatars/u3.jpg" alt="Jason Calder" className="w-3 h-3 rounded-full object-cover" />
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}







